import { EnderecoFieldGroupModel } from 'components/form'
import { EnderecoIndigenaFieldGroupModel } from 'components/form/field/enderecoindigena/model-enderecoIndigena'
import {
  CidadaoAldeadoInput,
  CidadaoInput,
  CondicoesSaudeAutorreferidasInput,
  EnderecoIndigenaInput,
  EnderecoInput,
  InformacoesSociodemograficasInput,
  InformacoesSocioEconomicasInput,
  SituacaoDeRuaInput,
  VinculacaoCidadaoFamiliaInput,
  VinculacaoCidadaoTerritorioInput,
} from 'graphql/types.generated'
import { ErrorObject } from 'util/validation'
import { booleanToSimNaoEnum } from 'view/cadastro-imovel/utils-cadastroImovel'

import {
  CidadaoAldeadoFormModel,
  CidadaoFormModel,
  CondicoesSaudeAutorreferidasFormModel,
  ContatosFormModel,
  DadosPessoaisFormModel,
  InformacoesSociodemograficasFormModel,
  InformacoesSocioEconomicasFormModel,
  NacionalidadeFormModel,
  ResponsabilidadeAcompanhamentoFormModel,
  SituacaoRuaFormModel,
} from './types/model-cidadao'

const normalizeSimNao = (value?: boolean | string | null): string | undefined =>
  value == null ? undefined : typeof value === 'string' ? value : booleanToSimNaoEnum(value)

const asStringError = (value?: string | ErrorObject<any> | null): string | undefined =>
  value == null ? undefined : typeof value === 'string' ? value : undefined

const convertDadosPessoaisBackendErrorsToForm = (
  input?: ErrorObject<CidadaoInput>
): ErrorObject<DadosPessoaisFormModel> => ({
  cpf: input?.cpf,
  cns: input?.cns,
  isCidadaoAldeado: input?.isCidadaoAldeado,
  nome: input?.nome,
  nomeSocial: input?.nomeSocial,
  dataNascimento: asStringError(input?.dataNascimento),
  sexo: input?.sexo,
  estadoCivil: input?.estadoCivil,
  tipoSanguineoEnum: input?.tipoSanguineoEnum,
  racaCor: input?.racaCor,
  etnia: input?.etniaId,
  nomeMae: input?.nomeMae,
  nomePai: input?.nomePai,
  desconheceNomeMae: input?.desconheceNomeMae,
  desconheceNomePai: input?.desconheceNomePai,
  nacionalidadeCidadao: converterNacionalidadeBackendErrorsToForm(input),
  desejaInformarOrientacaoSexual: normalizeSimNao(input?.desejaInformarOrientacaoSexual),
  desejaInformarIdentidadeGenero: normalizeSimNao(input?.desejaInformarIdentidadeGenero),
  identidadeGenero: input?.identidadeGenero,
  orientacaoSexual: input?.orientacaoSexual,
  faleceu: input?.faleceu,
  dataObito: asStringError(input?.dataObito),
  numeroDeclaracaoObito: input?.numeroDeclaracaoObito,
  justificativaNaoPossuiCpf: input?.justificativaNaoPossuiCpf ?? undefined,
  stNaoPossuiCpf: input?.stNaoPossuiCpf ?? undefined,
})

const converterNacionalidadeBackendErrorsToForm = (
  input?: ErrorObject<CidadaoInput>
): ErrorObject<NacionalidadeFormModel> => ({
  nacionalidade: input?.nacionalidade,
  municipioNascimento: input?.municipioNascimento,
  portariaNaturalizacao: input?.portariaNaturalizacao,
  dataNaturalizacao: asStringError(input?.dataNaturalizacao),
  paisNascimento: input?.paisNascimento,
  dataEntradaPais: asStringError(input?.dataEntradaPais),
})

const convertContatosBackendErrorsToForm = (input?: ErrorObject<CidadaoInput>): ErrorObject<ContatosFormModel> => ({
  telefoneResidencial: input?.telefoneResidencial,
  telefoneCelular: input?.telefoneCelular,
  telefoneContato: input?.telefoneContato,
  telefones: {
    telefoneResidencial: input?.telefoneResidencial,
    telefoneCelular: input?.telefoneCelular,
    telefoneContato: input?.telefoneContato,
  },
  email: input?.email,
})

const convertEnderecoBackendErrorsToForm = (
  cidadaoInput?: ErrorObject<CidadaoInput>,
  enderecoInput?: ErrorObject<EnderecoInput>
): ErrorObject<EnderecoFieldGroupModel> => ({
  bairro: enderecoInput?.bairro,
  cep: enderecoInput?.cep,
  complemento: enderecoInput?.complemento,
  logradouro: enderecoInput?.logradouro,
  municipio: enderecoInput?.municipio,
  numero: enderecoInput?.numero,
  pontoReferencia: enderecoInput?.pontoReferencia,
  semNumero: enderecoInput?.semNumero,
  tipoLogradouro: enderecoInput?.tipoLogradouro,
  uf: enderecoInput?.uf,
  pais: cidadaoInput?.paisNascimento,
  municipioResidenciaExterior: cidadaoInput?.municipioResidenciaExterior,
  microArea: cidadaoInput?.microarea,
  foraArea: cidadaoInput?.statusForaArea,
  numeroFamilia: cidadaoInput?.numeroFamilia,
})

const convertInformacoesSocioEconomicasBackendErrorsToForm = (
  input?: ErrorObject<InformacoesSocioEconomicasInput>
): ErrorObject<InformacoesSocioEconomicasFormModel> => ({
  alimentosAcabaramAntesTerDinheiroComprarMais: normalizeSimNao(
    input?.statusAlimentosAcabaramAntesTerDinheiroComprarMais
  ),
  comeuAlgunsAlimentosDinheiroAcabou: normalizeSimNao(input?.statusComeuAlgunsAlimentosQueTinhaDinheiroAcabou),
})

const convertInformacoesSociodemograficasBackendErrorsToForm = (
  input?: ErrorObject<InformacoesSociodemograficasInput>
): ErrorObject<InformacoesSociodemograficasFormModel> => ({
  escolaridade: input?.escolaridade,
  frequentaEscolaOuCreche: normalizeSimNao(input?.frequentaEscolaOuCreche),
  criancas0a9anosComQuemFica: input?.criancas0a9anosComQuemFica,
  situacaoMercadoTrabalho: input?.situacaoMercadoTrabalho,
  ocupacao: input?.ocupacaoCbo2002,
  possuiPlanoSaudePrivado: normalizeSimNao(input?.possuiPlanoSaudePrivado),
  frequentaCuidadorTradicional: normalizeSimNao(input?.frequentaCuidadorTradicional),
  participaAlgumGrupoComunitario: normalizeSimNao(input?.participaAlgumGrupoComunitario),
  isMembroComunidadeTradicional: normalizeSimNao(input?.isMembroComunidadeTradicional),
  povoComunidadeTradicional: input?.povoComunidadeTradicional,
  possuiDeficiencia: normalizeSimNao(input?.possuiDeficiencia),
  deficiencias: input?.deficiencias,
  isAtendimentoSocioeducativo: normalizeSimNao(input?.isAtendimentoSocioeducativo),
  tipoAtendimentoSocioeducativo: input?.tipoAtendimentoSocioeducativo,
  tempoAtendimentoSocioeducativo: input?.tempoAtendimentoSocioeducativo,
})

const convertSituacaoRuaBackendErrorsToForm = (
  input?: ErrorObject<SituacaoDeRuaInput>
): ErrorObject<SituacaoRuaFormModel> => ({
  tempoSituacaoRua: input?.tempoSituacaoDeRua,
  recebeAlgumBeneficio: normalizeSimNao(input?.recebeAlgumBeneficio),
  possuiReferenciaFamiliar: normalizeSimNao(input?.possuiReferenciaFamiliar),
  quantidadeAlimentacaoDiaria: input?.quantidadeAlimentacaoDiaria,
  origemAlimentacao: input?.origemAlimentacao,
  stAcompanhadoOutraInstituicao: normalizeSimNao(input?.stAcompanhadoOutraInstituicao),
  instituicaoAcompanhamento: input?.instituicaoAcompanhamento,
  possuiAcessoHigienePessoal: normalizeSimNao(input?.stAcessoHigienePessoal),
  higienePessoal: input?.higienePessoal,
  visitaFamiliarComFrequencia: normalizeSimNao(input?.stVisitaFamiliarComFrequencia),
  grauParentescoVisitaFamiliar: input?.grauParentescoVisitaFamiliar,
})

const convertcondicoesSaudeAutorreferidasBackendErrorsToForm = (
  input?: ErrorObject<CondicoesSaudeAutorreferidasInput>
): ErrorObject<CondicoesSaudeAutorreferidasFormModel> => ({
  stGestante: normalizeSimNao(input?.stGestante),
  maternidadeReferencia: input?.maternidadeReferencia,
  autodenominacaoPeso: input?.autodenominacaoPeso,
  stAcamado: normalizeSimNao(input?.stAcamado),
  stDomiciliar: normalizeSimNao(input?.stDomiciliar),
  stFumante: normalizeSimNao(input?.stFumante),
  stUsoAlcool: normalizeSimNao(input?.stUsoAlcool),
  stUsoOutrasDrogas: normalizeSimNao(input?.stUsoOutrasDrogas),
  stPraticasComplementares: normalizeSimNao(input?.stPraticasComplementares),
  stProblemaSaudeMental: normalizeSimNao(input?.stProblemaSaudeMental),
  stHipertensaoArterial: normalizeSimNao(input?.stHipertensaoArterial),
  stDiabetes: normalizeSimNao(input?.stDiabetes),
  stCancer: normalizeSimNao(input?.stCancer),
  stDerrame: normalizeSimNao(input?.stDerrame),
  stInfarto: normalizeSimNao(input?.stInfarto),
  stHanseniase: normalizeSimNao(input?.stHanseniase),
  stTuberculose: normalizeSimNao(input?.stTuberculose),
  stDoencaCardiaca: normalizeSimNao(input?.stDoencaCardiaca),
  doencaCardiaca: input?.doencaCardiaca,
  stProblemaRins: normalizeSimNao(input?.stProblemaRins),
  doencaRins: input?.doencaRins,
  stDoencaRespiratoria: normalizeSimNao(input?.stDoencaRespiratoria),
  doencasRespiratorias: input?.doencasRespiratorias,
  stInternacaoUltimos12Meses: normalizeSimNao(input?.stInternacaoUltimos12Meses),
  causaInternacaoUltimos12Meses: input?.causaInternacaoUltimos12Meses,
  stPlantasMedicinais: normalizeSimNao(input?.stPlantasMedicinais),
  plantasMedicinais: input?.plantasMedicinais,
  condicaoSaude1: input?.condicaoSaude1,
  condicaoSaude2: input?.condicaoSaude2,
  condicaoSaude3: input?.condicaoSaude3,
})

const converterCidadaoAldeadoBackendErrorsToForm = (
  cidadaoAldeadoInput?: ErrorObject<CidadaoAldeadoInput>,
  cidadaoInput?: ErrorObject<CidadaoInput>
): ErrorObject<CidadaoAldeadoFormModel> => ({
  funcaoSocial: cidadaoAldeadoInput?.funcaoSocial,
  aldeiaNascimento: cidadaoAldeadoInput?.aldeiaNascimentoId,
  localOcorrencia: cidadaoAldeadoInput?.localOcorrenciaId,
  nomeTradicional: cidadaoAldeadoInput?.nomeTradicional,
  responsavelLegal: cidadaoAldeadoInput?.responsavelLegal,
  stChefeFamilia: normalizeSimNao(cidadaoAldeadoInput?.stChefeFamilia),
  beneficios: cidadaoAldeadoInput?.beneficios,
  unidadeFunai: cidadaoAldeadoInput?.unidadeFunai,
  livro: cidadaoAldeadoInput?.livro,
  folha: cidadaoAldeadoInput?.folha,
  cadastroUnico: cidadaoAldeadoInput?.cadastroUnico,
  dtEmissao: asStringError(cidadaoAldeadoInput?.dtEmissao),
  ufNascimento: cidadaoAldeadoInput?.ufNascimentoId,
  dtReconhecimento: asStringError(cidadaoAldeadoInput?.dtReconhecimento),
  dtAusencia: asStringError(cidadaoAldeadoInput?.dtAusencia),
  dtRetorno: asStringError(cidadaoAldeadoInput?.dtRetorno),
  tipoLocalNascimento: cidadaoInput?.tipoLocalNascimento,
})

const convertEnderecoIndigenaBackendErrorsToForm = (
  input?: ErrorObject<CidadaoInput>,
  enderecoIndigena?: ErrorObject<EnderecoIndigenaInput>
): ErrorObject<EnderecoIndigenaFieldGroupModel> => ({
  dsei: enderecoIndigena?.dseiResidenciaId,
  poloBase: enderecoIndigena?.poloBaseResidenciaId,
  aldeia: enderecoIndigena?.aldeiaResidenciaId,
  municipio: enderecoIndigena?.municipioId,
  uf: enderecoIndigena?.ufId,
  numero: enderecoIndigena?.numero,
  numeroFamilia: input?.numeroFamilia,
})

const converterResponsabilidadeAcompanhamentoBackendErrorsToForm = (
  input?: ErrorObject<CidadaoInput>,
  vinculacaoFamilia?: ErrorObject<VinculacaoCidadaoFamiliaInput>,
  vinculacaoTerritorio?: ErrorObject<VinculacaoCidadaoTerritorioInput>
): ErrorObject<ResponsabilidadeAcompanhamentoFormModel> => ({
  microarea: vinculacaoTerritorio?.microarea,
  statusForaArea: input?.statusForaArea,
  statusCidadaoSaiuTerritorio: vinculacaoTerritorio?.saidaTerritorio,
  responsavel: vinculacaoFamilia?.cpfCnsResponsavelFamiliar,
  grauParentescoResponsavelFamiliar: vinculacaoFamilia?.grauParentescoResponsavelFamiliar,
  isResponsavelFamiliar: vinculacaoFamilia?.isResponsavelFamiliar,
  responsavelFamiliar: vinculacaoFamilia?.cpfCnsResponsavelFamiliar,
})

export const convertCidadaoBackendErrorsToForm = (
  validationErrors?: ErrorObject<CidadaoInput>
): ErrorObject<CidadaoFormModel> => {
  const input = validationErrors ?? {}

  return {
    dadosPessoais: convertDadosPessoaisBackendErrorsToForm(input),
    contatos: convertContatosBackendErrorsToForm(input),
    informacoesSocioEconomicas: convertInformacoesSocioEconomicasBackendErrorsToForm(
      input?.informacoesSocioEconomicas as InformacoesSocioEconomicasInput
    ),
    informacoesSociodemograficas: convertInformacoesSociodemograficasBackendErrorsToForm(
      input?.informacoesSociodemograficas as InformacoesSociodemograficasInput
    ),
    statusSituacaoRua: input?.stSituacaoDeRua,
    situacaoRua: convertSituacaoRuaBackendErrorsToForm(input?.situacaoDeRua as SituacaoDeRuaInput),
    condicoesSaudeAutorreferidas: convertcondicoesSaudeAutorreferidasBackendErrorsToForm(
      input?.condicoesSaudeAutorreferidas as CondicoesSaudeAutorreferidasInput
    ),
    cidadaoAldeado: converterCidadaoAldeadoBackendErrorsToForm(input?.cidadaoAldeadoInput as CidadaoAldeadoInput),
    responsabilidadeAcompanhamento: converterResponsabilidadeAcompanhamentoBackendErrorsToForm(
      input,
      input?.vinculacaoCidadaoFamilia as VinculacaoCidadaoFamiliaInput,
      input?.vinculacaoCidadaoTerritorio as VinculacaoCidadaoTerritorioInput
    ),
    endereco: convertEnderecoBackendErrorsToForm(input, input?.endereco as EnderecoInput),
    enderecoIndigena: convertEnderecoIndigenaBackendErrorsToForm(
      input,
      input?.enderecoIndigenaInput as EnderecoIndigenaInput
    ),
  } as ErrorObject<CidadaoFormModel>
}
